import { useMutation } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';

import { cancelOperation } from '@/api/operation';

import { useConfirmation } from '@/context';

import { useAppDispatch } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { signOut } from '@/redux/auth/actions';

import { User } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

import { Avatar } from './Avatar';
import { UserMenuOption } from './User.interface';
import { UserMenu } from './UserMenu';

import { AvatarButton } from './User.styled';

type LoggedInUserButtonProps = {
  user: User;
};

export function LoggedInUserButton({
  user,
}: Readonly<LoggedInUserButtonProps>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslate();
  const { showConfirmation } = useConfirmation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { mutate: cancelApplicationMutate } = useMutation({
    mutationKey: ['cancelOperation'],
    mutationFn: cancelOperation,
    onSuccess: () => {
      dispatch(signOut());
    },
  });

  const onTriggerButton = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onCancelApplication = () => {
    showConfirmation({
      title: t('cancelApplication.confirmTitle'),
      description: t('cancelApplication.confirmDescription'),
      primaryText: t('common.confirm'),
      secondaryText: t('common.cancel'),
      onCancel: () => {},
      onConfirm: () => {
        if (user.operationId) {
          cancelApplicationMutate({ operationId: user.operationId });
        }
      },
    });
  };

  const onOptionClick = (option: UserMenuOption) => {
    switch (option) {
      case 'sign-out':
        dispatch(signOut());
        break;
      case 'cancel-application':
        onCancelApplication();
        break;
    }
    onClose();
  };

  return (
    <>
      <AvatarButton
        onClick={onTriggerButton}
        {...getDataTestId('user-header-avatar')}
      >
        <Avatar>{user.firstName}</Avatar>
      </AvatarButton>
      <UserMenu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        onOptionClick={onOptionClick}
      />
    </>
  );
}
